import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Clock, Star, ArrowRight, Sparkles, Shield, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { businessInfo } from '../data/mock';

export const LomaLindaCA = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Mobile Car Detailing in Loma Linda, CA | Precision Mobile Detailing';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional mobile car detailing in Loma Linda, CA. Serving Loma Linda University, VA Medical Center area, and all neighborhoods. Premium auto detailing at your location.');
    }

    // Add structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Precision Mobile Detailing - Loma Linda",
      "image": "https://customer-assets.emergentagent.com/job_shine-mobile-14/artifacts/48ukm1vz_image.png",
      "@id": "",
      "url": window.location.href,
      "telephone": businessInfo.phone,
      "priceRange": "$$",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Loma Linda",
        "addressRegion": "CA",
        "addressCountry": "US"
      },
      "areaServed": {
        "@type": "City",
        "name": "Loma Linda",
        "addressRegion": "CA"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 34.0483,
        "longitude": -117.2612
      },
      "description": "Professional mobile car detailing service in Loma Linda, CA",
      "serviceType": ["Auto Detailing", "Car Detailing", "Mobile Detailing"]
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    { name: 'Full Detail Package', link: '/services' },
    { name: 'Essential Detail', link: '/services' },
    { name: 'Interior Reset', link: '/services' },
    { name: 'Maintenance Package', link: '/services' }
  ];

  const faqs = [
    {
      question: 'Can you detail my car at Loma Linda University Medical Center?',
      answer: 'Yes! We regularly provide mobile detailing services at the Loma Linda University Medical Center campus and surrounding medical facilities. Healthcare professionals love the convenience of having their car detailed while they work.'
    },
    {
      question: 'How does mobile detailing work in Loma Linda?',
      answer: 'We bring all our equipment, water, and supplies directly to your location in Loma Linda. Whether you are at home, at work, or at the university campus, we handle everything onsite and you do not need to do anything.'
    },
    {
      question: 'What is the typical timeframe for car detailing in Loma Linda?',
      answer: 'Depending on the service package and vehicle condition, most detailing jobs take between 2 to 6 hours. We work efficiently while maintaining the highest quality standards.'
    },
    {
      question: 'Do you serve residential areas in Loma Linda?',
      answer: 'Absolutely! We detail cars at homes, apartments, condos, and residential communities throughout Loma Linda. Just provide us with a safe parking spot and we will take care of the rest.'
    },
    {
      question: 'Is mobile auto detailing more expensive than going to a shop?',
      answer: 'Our mobile detailing prices are competitive with traditional shops, but you save time and hassle. No driving, no waiting rooms, just professional results delivered to your Loma Linda location.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-gray-900 to-black text-white">
        <div className="absolute inset-0 bg-[url('https://i.ytimg.com/vi/38EWcpWsBfQ/maxresdefault.jpg')] bg-cover bg-center opacity-20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl">
            <div className="flex items-center gap-2 mb-6">
              <MapPin className="w-5 h-5" />
              <span className="text-sm font-semibold uppercase tracking-wider">Loma Linda, California</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Mobile Car Detailing in Loma Linda, CA
            </h1>
            
            <p className="text-xl mb-8 text-gray-300 leading-relaxed">
              Premium mobile auto detailing for Loma Linda University, the VA Medical Center area, and all Loma Linda neighborhoods. 
              We come to you—home, hospital, campus, or workplace.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button asChild size="lg" className="bg-white text-black hover:bg-gray-100 text-lg px-8 py-6">
                <Link to="/booking">
                  Book Loma Linda Detailing <ArrowRight className="ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6 border-white text-white hover:bg-white hover:text-black transition-all duration-300">
                <a href={`tel:${businessInfo.phone}`}>
                  <Phone className="mr-2" /> {businessInfo.phone}
                </a>
              </Button>
            </div>

            <div className="flex flex-wrap gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                <span>5.0 Rating • 30 Reviews</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span>{businessInfo.daysOpen} • {businessInfo.hours}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Mobile Detailing in Loma Linda */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Professional Auto Detailing for Loma Linda</h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Precision Mobile Detailing brings expert car care directly to Loma Linda residents, medical professionals, 
                students, and businesses. Whether you're at Loma Linda University Health, the VA Medical Center, 
                or anywhere in the city, we make premium detailing effortless.
              </p>
              <p>
                We know your time is valuable—especially if you work long shifts at the hospital or juggle a busy academic schedule. 
                That's why we come to you. Schedule your detail, go about your day, and return to a vehicle that looks and feels brand new.
              </p>
              <p>
                Our mobile detailing service matches the quality of top-tier detailing shops, but with the ultimate convenience: 
                no waiting rooms, no driving across town, just professional results delivered wherever you are in Loma Linda.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Perfect For Busy Professionals */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">Perfect for Loma Linda's Busy Professionals</h2>
            <p className="text-center text-gray-600 mb-12 text-lg">
              Mobile car detailing is ideal for healthcare workers, students, faculty, and residents with packed schedules.
            </p>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-3">Healthcare Workers</h3>
                <p className="text-gray-600">Long shifts at the hospital? We'll detail your car in the parking lot while you work. Return to a pristine vehicle.</p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-3">Students & Faculty</h3>
                <p className="text-gray-600">Between classes, research, and study sessions, who has time for a car wash? We bring detailing to campus.</p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-3">Residents & Families</h3>
                <p className="text-gray-600">Stay home with your family while we take care of your vehicle. Convenient, professional, and hassle-free.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services in Loma Linda */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">Mobile Detailing Services in Loma Linda</h2>
            <p className="text-center text-gray-600 mb-12 text-lg">
              From maintenance washes to full interior and exterior transformations, we have a package for every need.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-12">
              {services.map((service, idx) => (
                <Card key={idx} className="border hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <CheckCircle className="w-6 h-6 text-black flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="text-xl font-bold mb-2">{service.name}</h3>
                        <Link to={service.link} className="text-black font-semibold hover:underline">
                          Learn More →
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center">
              <Button asChild size="lg" className="cta-button text-lg px-8 py-6">
                <Link to="/services">
                  View Full Service Menu & Pricing
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Why Loma Linda Trusts Precision Mobile Detailing</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Clock className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-4">Flexible Scheduling</h3>
                  <p className="text-gray-600">We work around your schedule—early mornings, evenings, and weekends available for busy Loma Linda professionals.</p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Sparkles className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-4">Showroom Finish</h3>
                  <p className="text-gray-600">Meticulous attention to detail using premium products. Your vehicle will look better than the day you bought it.</p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Shield className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-4">Trusted & Professional</h3>
                  <p className="text-gray-600">5.0-star service from a detailer who cares about quality and customer satisfaction. Reliable and punctual, every time.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area within Loma Linda */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">Areas We Serve in Loma Linda</h2>
            <p className="text-center text-gray-600 mb-8 text-lg">
              Mobile car detailing throughout Loma Linda and neighboring communities.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              {['Loma Linda University', 'VA Medical Center Area', 'Campus District', 'Mountain View', 'San Timoteo Canyon', 'Residential Areas'].map((area, idx) => (
                <span key={idx} className="bg-gray-100 px-6 py-3 rounded-full font-semibold text-gray-700">
                  {area}
                </span>
              ))}
            </div>

            <p className="text-center text-gray-600 mt-8">
              We also serve nearby <Link to="/service-areas/redlands-ca" className="text-black font-semibold hover:underline">Redlands</Link> and <Link to="/service-areas/yucaipa-ca" className="text-black font-semibold hover:underline">Yucaipa</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Common Questions About Mobile Detailing in Loma Linda</h2>
            
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={idx} className="border shadow-sm">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-3">{faq.question}</h3>
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Schedule Your Mobile Detail in Loma Linda</h2>
          <p className="text-xl mb-10 max-w-2xl mx-auto text-gray-300">
            Convenient, professional car detailing delivered to your location. Book your appointment today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-black hover:bg-gray-100 text-lg px-8 py-6">
              <Link to="/booking">
                Book Now <ArrowRight className="ml-2" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6 border-white text-white hover:bg-white hover:text-black transition-all duration-300">
              <Link to="/service-areas">
                View All Service Areas
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LomaLindaCA;
